import type { RequestContext } from "../context/request-context.js";
import type { Equipment } from "../../equipment/equipment-domain.js";
import { buildGeneratedLineList, type GeneratedLineList } from "../../domain/inspection-book/line-list.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import { documentEngine } from "../../infrastructure/documents/document-engine.js";

/**
 * FONTE ÚNICA DA LISTA DE LINHAS (comando complementar, item 2): um único
 * serviço resolve "Sistema → Linhas vinculadas" pelo cadastro oficial de
 * Equipamentos (`Equipment.pipingSystemId`) e alimenta os três consumidores
 * — Relatório de Integridade, Book de Inspeção e o PDF independente da
 * Lista de Linhas — sem que nenhum deles repita a orquestração ou monte uma
 * segunda versão dos dados. A composição em si (ordenação, formatação,
 * "NÃO INFORMADO", referências consolidadas) é a mesma função pura de
 * `buildGeneratedLineList` (domain/inspection-book/line-list.ts).
 *
 * Operação de leitura/consolidação: nunca cria, altera ou recalcula nada do
 * cadastro técnico.
 */

export interface LineListPdfContext {
  clientLabel?: string;
  osLabel?: string;
  projectLabel?: string;
  unitAreaLabel?: string;
}

/** O que este serviço precisa do EquipmentService — qualquer implementação compatível serve (produção ou teste). */
export interface EquipmentLookup {
  getEquipment(context: RequestContext, id: string): Promise<Equipment | null>;
  listEquipments(context: RequestContext, filter?: { status?: string | "ALL"; clientId?: string }): Promise<Equipment[]>;
}

export interface LineListRelatedLookups {
  listSafetyDevices?(context: RequestContext, filters?: { protectedEquipmentId?: string; areaId?: string; status?: string }): Promise<readonly any[]>;
  listInstruments?(context: RequestContext, filters?: { equipmentId?: string; areaId?: string; status?: string }): Promise<readonly any[]>;
}

export class PipingSystemLineListService {
  constructor(
    private readonly equipment: EquipmentLookup,
    private readonly related?: LineListRelatedLookups
  ) {}

  /** Resolve o Sistema pelo id e sua Lista de Linhas. Lança quando não é um Sistema de Tubulação — uso direto (ex.: PDF independente). */
  async resolve(context: RequestContext, systemId: string): Promise<{ system: Equipment; lineList: GeneratedLineList }> {
    const system = await this.equipment.getEquipment(context, systemId);
    if (!system) {
      throw new AppError(ErrorCode.NotFound, "Piping system not found", { safeMessage: "Sistema de Tubulação não encontrado." });
    }
    if (system.equipmentType !== "PIPING_SYSTEM") {
      throw new AppError(ErrorCode.Validation, "Not a piping system", { safeMessage: "O item selecionado não é um Sistema de Tubulação." });
    }
    return { system, lineList: await this.buildForKnownSystem(context, system) };
  }

  /**
   * Usada pelo RI e pelo Book, que já têm o Equipment em mãos: devolve `null`
   * (sem lançar) quando não é Sistema — a seção/aba simplesmente não aparece
   * para outros tipos de contexto.
   */
  async buildIfSystem(context: RequestContext, equipment: Equipment | null | undefined): Promise<GeneratedLineList | null> {
    if (!equipment || equipment.equipmentType !== "PIPING_SYSTEM") return null;
    return this.buildForKnownSystem(context, equipment);
  }

  private async buildForKnownSystem(context: RequestContext, system: Equipment): Promise<GeneratedLineList> {
    const equipments = await this.equipment.listEquipments(context, {});
    const [safetyDevices, instruments] = await Promise.all([
      this.related?.listSafetyDevices ? this.related.listSafetyDevices(context, { status: "ACTIVE" }) : Promise.resolve([]),
      this.related?.listInstruments ? this.related.listInstruments(context, { status: "ACTIVE" }) : Promise.resolve([])
    ]);
    return buildGeneratedLineList(system, equipments, safetyDevices as any, instruments as any);
  }

  private snapshotOf(system: Equipment, lineList: GeneratedLineList, pdfContext: LineListPdfContext): DocumentSnapshot {
    return {
      id: `LINE_LIST-${system.tag || system.id}`,
      type: "LINE_LIST",
      revision: "00",
      clientId: String(system.clientId),
      data: { system, generatedLineList: lineList, pdfContext },
      createdAt: new Date()
    };
  }

  /** PDF INDEPENDENTE — não exige gerar o Book completo para obter a Lista. */
  async generatePdf(context: RequestContext, systemId: string, pdfContext: LineListPdfContext = {}): Promise<string> {
    const { system, lineList } = await this.resolve(context, systemId);
    return documentEngine.generatePdf(this.snapshotOf(system, lineList, pdfContext));
  }

  async downloadPdf(context: RequestContext, systemId: string, pdfContext: LineListPdfContext = {}): Promise<Uint8Array> {
    const { system, lineList } = await this.resolve(context, systemId);
    const safeTag = String(system.tag || system.id).replace(/[^A-Za-z0-9._-]+/g, "_");
    return documentEngine.downloadPdf(this.snapshotOf(system, lineList, pdfContext), `LISTA_DE_LINHAS_${safeTag}.pdf`);
  }
}
